'use client';

import { c as _c } from "react/compiler-runtime";
import { type Context, createContext, type RefObject, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPaintedMarketShareDisplayStore, type PaintedMarketShareDisplayStore, type PaintedMarketShareSnapshot, type PaintedMarketShareStructure } from '@/lib/chart/painted-market-share-display';
import { isTerminalChartStatus } from '@/lib/chart/terminal-chart-lifecycle';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { useChartSettingsStore } from '@/stores/chart-settings';
const PaintedMarketShareDisplayContext: Context<PaintedMarketShareDisplayStore | null> = createContext<PaintedMarketShareDisplayStore | null>(null);
interface AuthoritativePresentation {
  readonly enabled: boolean;
  readonly authoritativeMode: boolean;
}
const INACTIVE_PRESENTATION: AuthoritativePresentation = {
  enabled: false,
  authoritativeMode: false
};
const AuthoritativePresentationContext: Context<AuthoritativePresentation> = createContext(INACTIVE_PRESENTATION);
export function PaintedMarketShareDisplayProvider(t0) {
  const $ = _c(13);
  const {
    marketPubkey,
    children,
    store: providedStore,
    lifecycleStatus
  } = t0;
  const network = useActiveNetwork();
  const chartMode = useChartSettingsStore(_temp);
  const marketShareSelected = chartMode === "consensus";
  let t1;
  if ($[0] !== lifecycleStatus || $[1] !== marketShareSelected) {
    t1 = marketShareSelected && !isTerminalChartStatus(lifecycleStatus);
    $[0] = lifecycleStatus;
    $[1] = marketShareSelected;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== marketShareSelected || $[4] !== t1) {
    t2 = {
      enabled: t1,
      authoritativeMode: marketShareSelected
    };
    $[3] = marketShareSelected;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const presentation = t2;
  const routeKey = `${network}:${marketPubkey}`;
  let t3;
  if ($[6] !== children || $[7] !== providedStore || $[8] !== routeKey) {
    t3 = <PaintedMarketShareDisplayScope key={routeKey} providedStore={providedStore}>{children}</PaintedMarketShareDisplayScope>;
    $[6] = children;
    $[7] = providedStore;
    $[8] = routeKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== presentation || $[11] !== t3) {
    t4 = <AuthoritativePresentationContext value={presentation}>{t3}</AuthoritativePresentationContext>;
    $[10] = presentation;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp(state) {
  return state.chartMode;
}
function PaintedMarketShareDisplayScope(t0) {
  const $ = _c(3);
  const {
    children,
    providedStore
  } = t0;
  const [ownedStore] = useState(createPaintedMarketShareDisplayStore);
  const store = providedStore ?? ownedStore;
  let t1;
  if ($[0] !== children || $[1] !== store) {
    t1 = <PaintedMarketShareDisplayContext value={store}>{children}</PaintedMarketShareDisplayContext>;
    $[0] = children;
    $[1] = store;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function usePaintedMarketShareDisplayStore() {
  const store = useContext(PaintedMarketShareDisplayContext);
  if (store === null) {
    throw new Error("PaintedMarketShareDisplayProvider is required");
  }
  return store;
}
export function useAuthoritativePaintedDisplayCandidate() {
  const presentation = useAuthoritativeMarketSharePresentation();
  const structure = usePaintedMarketShareStructure();
  return presentation.enabled || presentation.authoritativeMode && structure.identityKey !== null;
}
export function useAuthoritativeMarketSharePresentation() {
  return useContext(AuthoritativePresentationContext);
}
const INACTIVE_SERVER_STRUCTURE: PaintedMarketShareStructure = {
  identityKey: null,
  availability: 'inactive',
  outcomeOrder: [],
  leaderOutcomeIndex: null,
  thresholdReached: false,
  forecast: null,
  forecastUpdating: false,
  terminalGeometryRevision: null
};
const subscribeToNothing = (): (() => void) => (): void => {};
const getInactiveStructure = (): PaintedMarketShareStructure => INACTIVE_SERVER_STRUCTURE;
export function usePaintedMarketShareStructure() {
  const store = useContext(PaintedMarketShareDisplayContext);
  return useSyncExternalStore(store?.subscribeStructure ?? subscribeToNothing, store?.getStructureSnapshot ?? getInactiveStructure, getInactiveStructure);
}
export interface PaintedMarketShareLeafValue {
  readonly text: string;
  readonly accessibilityLabel?: string;
  readonly tone?: 'default' | 'urgent';
}
export function PaintedMarketShareLeaf(t0) {
  const $ = _c(9);
  const {
    fallback,
    format,
    className,
    accessibilityTargetRef,
    testId
  } = t0;
  const store = usePaintedMarketShareDisplayStore();
  const leafRef = useRef(null);
  const lastValueRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== accessibilityTargetRef || $[1] !== format || $[2] !== store) {
    t1 = () => {
      const apply = () => {
        const snapshot = store.getPaintSnapshot();
        if (snapshot === null) {
          return;
        }
        const value = format(snapshot);
        const previous = lastValueRef.current;
        const leaf = leafRef.current;
        const accessibilityTarget = accessibilityTargetRef?.current;
        if (previous?.text === value.text && previous.accessibilityLabel === value.accessibilityLabel && previous.tone === value.tone && leaf?.textContent === value.text && leaf.dataset.tone === value.tone && (value.accessibilityLabel === undefined || accessibilityTarget?.getAttribute("aria-label") === value.accessibilityLabel)) {
          return;
        }
        lastValueRef.current = value;
        if (leaf === null) {
          return;
        }
        if (leaf.textContent !== value.text) {
          leaf.textContent = value.text;
        }
        if (leaf.dataset.tone !== value.tone) {
          if (value.tone === undefined) {
            delete leaf.dataset.tone;
          } else {
            leaf.dataset.tone = value.tone;
          }
        }
        if (accessibilityTarget !== null && accessibilityTarget !== undefined && value.accessibilityLabel !== undefined && accessibilityTarget.getAttribute("aria-label") !== value.accessibilityLabel) {
          accessibilityTarget.setAttribute("aria-label", value.accessibilityLabel);
        }
      };
      apply();
      return store.subscribePaint(apply);
    };
    t2 = [accessibilityTargetRef, format, store];
    $[0] = accessibilityTargetRef;
    $[1] = format;
    $[2] = store;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== className || $[6] !== fallback || $[7] !== testId) {
    t3 = <span ref={leafRef} className={className} data-testid={testId} suppressHydrationWarning={true}>{fallback}</span>;
    $[5] = className;
    $[6] = fallback;
    $[7] = testId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}